import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Dialog, Stack, t, toast } from '@kanbios/ui';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { QualificationDraft, QualificationFields, qualificationComplete } from './QualificationFields';
import { pendingLabel } from '../PendingButton';
import { changeRef } from './PullRequestNotice';

/** « Modifier la qualification » (SPEC-006 RG-006-4): opens a pull request, Administrator only. */
export function QualificationDialog({ open, onClose, project, current }: {
  open: boolean;
  onClose: () => void;
  project: { name: string; title: string };
  current: QualificationDraft;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<QualificationDraft>(current);
  const [tried, setTried] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setTried(true);
    if (!qualificationComplete(draft)) return;
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/projects/{project}/qualification', {
        params: { path: { project: project.name } },
        body: { criticality: draft.criticality as 'critical', qualification: { healthData: draft.healthData!, dora: draft.dora!, sensitivePersonalData: draft.sensitivePersonalData!, internetExposed: draft.internetExposed! } },
      }));
      toast({ title: t('qualification.opened', { ref: changeRef(pullRequest) }), tone: 'pos' });
      await queryClient.invalidateQueries({ queryKey: ['project', project.name] });
      onClose();
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={t('qualification.dialogTitle')}
      description={project.title}
      confirmLabel={pendingLabel(t('qualification.confirm'), sending)}
      cancelLabel={t('qualification.cancel')}
      confirmDisabled={sending}
      onConfirm={submit}
    >
      <Stack gap={16}>
        {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
        {tried && !qualificationComplete(draft) && <Alert tone="neg">{t('qualification.incomplete')}</Alert>}
        <QualificationFields value={draft} onChange={setDraft} errors={tried} />
      </Stack>
    </Dialog>
  );
}
